import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { components } from "@/api/schema";
import { SUPPLIER_KIND_LABELS, type SourcingOption } from "@/api/sourcing";
import { Button } from "@/components/ui/button";
import { describePaymentTerms } from "@/features/manufacturers/payment-terms";
import {
  bestDiscount,
  describeOrderFees,
  describeOtherFees,
  FREIGHT_BOOKING_LABELS,
} from "@/features/manufacturers/source-glance";
import { freightRulesProgram } from "@/lib/manufacturer-list";
import { cn } from "@/lib/utils";

type Manufacturer = components["schemas"]["ManufacturerDetailRead"];

function leadTime(days: number | null | undefined): string {
  return days != null
    ? `${days} business ${days === 1 ? "day" : "days"}`
    : "Not set";
}

/**
 * What we get buying this manufacturer through its preferred source: the best discount and the
 * freight policy first, then the few terms that decide an order (USA-1271). The preferred source
 * buys on the manufacturer's live discounts and freight program; its own terms mirror them.
 */
export function ManufacturerGlance({
  mfr,
  onOpenTerms,
}: {
  mfr: Manufacturer;
  onOpenTerms: () => void;
}) {
  const preferred: SourcingOption | undefined = [
    ...(mfr.sourcing_options ?? []),
  ].sort((a, b) => a.rank - b.rank)[0];
  const terms = preferred ?? mfr;
  const discount = bestDiscount(
    (mfr.discounts ?? []).map((category) => ({
      chain: category.discount,
      label: category.name ?? category.discount_id,
    })),
  );
  const freight = freightRulesProgram(mfr.freight_rules ?? []);
  const booking = FREIGHT_BOOKING_LABELS[terms.freight_booking ?? "auto"];
  const source = preferred?.supplier
    ? [
        preferred.supplier.name,
        [
          SUPPLIER_KIND_LABELS[preferred.supplier.kind],
          preferred.buying_group ? `via ${preferred.buying_group.name}` : null,
        ]
          .filter(Boolean)
          .join(" "),
      ]
    : ["Direct", preferred?.buying_group ? `via ${preferred.buying_group.name}` : "From the manufacturer"];
  const facts: { label: string; value: string; note?: string | null }[] = [
    { label: "Payment", value: describePaymentTerms(terms) },
    { label: "Lead time", value: leadTime(terms.lead_time_days) },
    {
      label: "Order minimum",
      value: describeOrderFees(
        terms.small_order_policy_type,
        terms.small_order_threshold,
        terms.small_order_fee,
      ),
      note: describeOtherFees((mfr.fee_rules ?? []).length),
    },
    { label: "POs go to", value: mfr.po_recipient_email ?? "Not set" },
  ];

  return (
    <section
      aria-labelledby="mfr-glance"
      className="overflow-hidden rounded-xl border bg-card"
    >
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2">
        <h2 id="mfr-glance" className="min-w-0 text-sm">
          <span className="text-muted-foreground">Buying through </span>
          {preferred?.supplier ? (
            <Link
              to="/suppliers/$supplierId"
              params={{ supplierId: preferred.supplier.uuid }}
              className="font-semibold text-primary hover:underline"
            >
              {source[0]}
            </Link>
          ) : (
            <span className="font-semibold">{source[0]}</span>
          )}
          <span className="text-muted-foreground"> · {source[1]}</span>
        </h2>
        <Button
          size="sm"
          variant="ghost"
          className="-mr-2 shrink-0"
          onClick={onOpenTerms}
        >
          All terms
          <ChevronRight className="size-4" />
        </Button>
      </div>

      <div className="grid divide-y sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        <div className="px-4 py-4">
          <p className="text-sm text-muted-foreground">Best discount</p>
          {discount ? (
            <>
              <p className="mt-1 font-mono text-3xl font-semibold tracking-tight tabular-nums">
                {discount.chain}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {discount.percentOff} list
                {discount.category ? ` · ${discount.category}` : ""}
              </p>
            </>
          ) : (
            <p className="mt-1 text-xl font-semibold text-muted-foreground sm:text-2xl">
              No discount
            </p>
          )}
        </div>
        <div className="px-4 py-4">
          <p className="text-sm text-muted-foreground">Freight</p>
          <p
            className={cn(
              "mt-1 text-xl font-semibold tracking-tight sm:text-2xl",
              !freight && "text-muted-foreground",
            )}
          >
            {freight?.text ?? "No free freight program"}
          </p>
          {(freight?.more || booking) && (
            <p className="mt-1 text-sm text-muted-foreground">
              {[freight?.more ? "More rules in All terms" : null, booking]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
        </div>
      </div>

      <dl className="grid grid-cols-2 border-t lg:grid-cols-4">
        {facts.map((fact, index) => (
          <div
            key={fact.label}
            className={cn(
              "min-w-0 px-4 py-2.5",
              index % 2 === 1 && "border-l",
              index >= 2 && "max-lg:border-t",
              index === 2 && "lg:border-l",
            )}
          >
            <dt className="text-xs text-muted-foreground">{fact.label}</dt>
            <dd
              className={cn(
                "truncate text-sm font-medium",
                ["Not set", "None", "Not recorded"].includes(fact.value) &&
                  "font-normal text-muted-foreground",
              )}
              title={fact.value}
            >
              {fact.value}
            </dd>
            {fact.note && (
              <dd className="truncate text-xs text-muted-foreground">
                {fact.note}
              </dd>
            )}
          </div>
        ))}
      </dl>
    </section>
  );
}
